/**
 * State model for the multi-file add modal (Inspirations, Visualizations).
 *
 * Deliberately React-free and side-effect-free: `jest.config.cjs` forbids
 * importing any module that reads `import.meta.env` (which rules out
 * `lib/api-client`), so keeping the list logic here is what makes it
 * testable at all. Object URLs are created and revoked by the row
 * component, not here — this module never touches the DOM.
 */

type PickedFileStatus = "ready" | "uploading" | "done" | "failed"

export interface PickedFile {
	/** Stable React key; unique for the lifetime of the page. */
	id: string
	file: File
	/** Media kind of the content type — "image" | "pdf" | "video". */
	kind: string
	title: string
	description: string
	status: PickedFileStatus
}

/**
 * Why a picked file never made it into the list. Every reason is surfaced to
 * the user next to the filename — a silently dropped file reads as a bug.
 */
export type RejectionReason =
	| "unsupported"
	| "tooLarge"
	| "duplicate"
	| "batchLimit"
	| "quotaFull"

export interface Rejection {
	name: string
	reason: RejectionReason
}

export interface AddFilesOptions {
	/** Media kind for a content type, or `undefined` if we don't accept it. */
	kindForContentType: (contentType: string) => string | undefined
	/** Upload cap for a kind, in bytes. Per-kind, never one flat number. */
	maxBytesForKind: (kind: string) => number
	/** How many rows the list may hold at once. */
	maxFiles: number
	/** Items the project already holds server-side. */
	existingCount: number
	/** The server's per-project anti-flood quota. */
	quota: number
	/** Server cap on a title, so an auto-filled one can never 400. */
	titleMax: number
}

/**
 * Monotonic row ids. A counter rather than `crypto.randomUUID()` so the
 * module stays deterministic under jest and free of environment probes.
 */
let sequence = 0

function nextId(): string {
	sequence += 1
	return `picked-${sequence}`
}

/** Identity of a file's bytes, for duplicate detection across picks. */
function fingerprint(file: File): string {
	return `${file.name}:${file.size}:${file.lastModified}`
}

/**
 * Filename without its extension — a better default title than "".
 *
 * Clamped to the server's limit: the row input's `maxLength` only blocks
 * further typing, it does not truncate a value we set programmatically, so
 * an over-long filename would otherwise be POSTed as-is and rejected.
 */
function titleFromFilename(name: string, max: number): string {
	return name.replace(/\.[^./\\]+$/, "").slice(0, max)
}

/**
 * Validate and append picked files. Returns the new list plus everything
 * that was refused, in pick order. Never mutates the input.
 *
 * Order of checks per file: duplicate, then capacity (batch cap, then
 * project quota), then the file itself (kind, size). Capacity comes before
 * validation because when there is no room the file's own properties are
 * beside the point.
 */
export function addFiles(
	rows: readonly PickedFile[],
	incoming: readonly File[],
	options: AddFilesOptions,
): { rows: PickedFile[]; rejected: Rejection[] } {
	const next = [...rows]
	const rejected: Rejection[] = []
	const seen = new Set(next.map((row) => fingerprint(row.file)))
	// Rows that still have to be created server-side. `done` rows are already
	// counted by `existingCount` (the list refetches when a batch settles), so
	// counting them here too would refuse files the project has room for.
	let awaitingUpload = next.filter((row) => row.status !== "done").length

	for (const file of incoming) {
		const print = fingerprint(file)
		if (seen.has(print)) {
			rejected.push({ name: file.name, reason: "duplicate" })
			continue
		}
		if (next.length >= options.maxFiles) {
			rejected.push({ name: file.name, reason: "batchLimit" })
			continue
		}
		if (options.existingCount + awaitingUpload >= options.quota) {
			rejected.push({ name: file.name, reason: "quotaFull" })
			continue
		}
		const kind = options.kindForContentType(file.type)
		if (!kind) {
			rejected.push({ name: file.name, reason: "unsupported" })
			continue
		}
		if (file.size > options.maxBytesForKind(kind)) {
			rejected.push({ name: file.name, reason: "tooLarge" })
			continue
		}
		seen.add(print)
		awaitingUpload += 1
		next.push({
			id: nextId(),
			file,
			kind,
			title: titleFromFilename(file.name, options.titleMax),
			description: "",
			status: "ready",
		})
	}

	return { rows: next, rejected }
}

export function removeFile(
	rows: readonly PickedFile[],
	id: string,
): PickedFile[] {
	return rows.filter((row) => row.id !== id)
}

/**
 * Edit a row's metadata. Rows mid-upload or already uploaded are locked —
 * their title and description have been sent, so accepting an edit here
 * would show the user a value the server does not have.
 */
export function updateFile(
	rows: readonly PickedFile[],
	id: string,
	patch: { title?: string; description?: string },
): PickedFile[] {
	return rows.map((row) => {
		if (row.id !== id) return row
		if (row.status === "uploading" || row.status === "done") return row
		return {
			...row,
			title: patch.title ?? row.title,
			description: patch.description ?? row.description,
		}
	})
}

export function setStatus(
	rows: readonly PickedFile[],
	id: string,
	status: PickedFileStatus,
): PickedFile[] {
	return rows.map((row) => (row.id === id ? { ...row, status } : row))
}

/** Re-arm the failed rows for a retry, leaving successful ones alone. */
export function resetFailed(rows: readonly PickedFile[]): PickedFile[] {
	return rows.map((row) =>
		row.status === "failed" ? { ...row, status: "ready" } : row,
	)
}

/** Rows whose fields the user may still change. */
export function editableRows(rows: readonly PickedFile[]): PickedFile[] {
	return rows.filter(
		(row) => row.status !== "uploading" && row.status !== "done",
	)
}

export function countByStatus(
	rows: readonly PickedFile[],
): Record<PickedFileStatus, number> {
	const counts: Record<PickedFileStatus, number> = {
		ready: 0,
		uploading: 0,
		done: 0,
		failed: 0,
	}
	for (const row of rows) counts[row.status] += 1
	return counts
}
